import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { isPlatformServer, isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class CoursePageResolver implements Resolve<any> {
  constructor(
    private http: HttpClient,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  private useTransferState<T>(key: string, observable$: Observable<T>): Observable<T> {
    const STATE_KEY = makeStateKey<T>(key);
    if (this.transferState.hasKey(STATE_KEY)) {
      return of(this.transferState.get<T>(STATE_KEY, null as any));
    }

    return observable$.pipe(
      map((data) => {
        if (isPlatformServer(this.platformId)) {
          this.transferState.set(STATE_KEY, data);
        }
        return data;
      })
    );
  }

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    const lang = route.url[0]?.path;
    const courseName = route.url[2]?.path;
    const video = route.url.length > 3 ? route.url[3].path : '';

    if (!lang || !courseName) {
      return of(null);
    }

    const contentUrl = `/assets/${environment.projectName}/content/${lang}/course-page/courses-data.json`;
    const metadataUrl = `/assets/${environment.projectName}/content/${lang}/course-page/meta-data.json`;

    return forkJoin({
      course: this.useTransferState(
        `course-content-${lang}-${courseName}`,
        this.http.get<any>(contentUrl).pipe(
          map((data) => {
            let course = data.find(
              (service: any) =>
                service.title.toLowerCase().replace(/ /g, '-') === courseName.toLowerCase()
            );

            if (course) {
              let playedLessons: string[] = [];
              if (isPlatformBrowser(this.platformId)) {
                const stored = localStorage.getItem(`playedLessons-${courseName}`);
                playedLessons = stored ? JSON.parse(stored) : [];
              }

              course.courseList.forEach((chapter: any) => {
                chapter.lessons.forEach((lesson: any) => {
                  const videoFileName = this.getVideoFileName(lesson.path);
                  lesson.played = playedLessons.includes(videoFileName);
                });
              });

              if (video) {
                for (let chapterIndex = 0; chapterIndex < course.courseList.length; chapterIndex++) {
                  const chapter = course.courseList[chapterIndex];

                  for (let lessonIndex = 0; lessonIndex < chapter.lessons.length; lessonIndex++) {
                    const lesson = chapter.lessons[lessonIndex];
                    const videoFileName = this.getVideoFileName(lesson.path);

                    if (videoFileName === video) {
                      const nextLesson =
                        lessonIndex + 1 < chapter.lessons.length
                          ? chapter.lessons[lessonIndex + 1]
                          : chapterIndex + 1 < course.courseList.length
                          ? course.courseList[chapterIndex + 1].lessons[0]
                          : null;

                      const prevLesson =
                        lessonIndex - 1 >= 0
                          ? chapter.lessons[lessonIndex - 1]
                          : chapterIndex - 1 >= 0
                          ? course.courseList[chapterIndex - 1].lessons[
                              course.courseList[chapterIndex - 1].lessons.length - 1
                            ]
                          : null;

                      lesson.played = true;

                      if (isPlatformBrowser(this.platformId)) {
                        if (!playedLessons.includes(videoFileName)) {
                          playedLessons.push(videoFileName);
                          localStorage.setItem(
                            `playedLessons-${courseName}`,
                            JSON.stringify(playedLessons)
                          );
                        }
                        localStorage.setItem(`lastWatched-${courseName}`, videoFileName);
                      }

                      course.videoLesson = {
                        ...lesson,
                        currentIndex: lessonIndex,
                        currentChapterIndex: chapterIndex,
                        nextLesson,
                        prevLesson,
                      };
                    }
                  }
                }
              } else {
                let lastWatched: string | null = null;
                if (isPlatformBrowser(this.platformId)) {
                  lastWatched = localStorage.getItem(`lastWatched-${courseName}`);
                }

                let targetLesson: any = null;
                let targetChapterIndex = 0;
                let targetLessonIndex = 0;

                if (lastWatched) {
                  outer: for (let ci = 0; ci < course.courseList.length; ci++) {
                    for (let li = 0; li < course.courseList[ci].lessons.length; li++) {
                      const fn = this.getVideoFileName(course.courseList[ci].lessons[li].path);
                      if (fn === lastWatched) {
                        targetLesson = course.courseList[ci].lessons[li];
                        targetChapterIndex = ci;
                        targetLessonIndex = li;
                        break outer;
                      }
                    }
                  }
                }

                if (!targetLesson) {
                  targetLesson = course.courseList[0].lessons[0];
                  targetChapterIndex = 0;
                  targetLessonIndex = 0;
                }

                const chapter = course.courseList[targetChapterIndex];
                const nextLesson =
                  targetLessonIndex + 1 < chapter.lessons.length
                    ? chapter.lessons[targetLessonIndex + 1]
                    : targetChapterIndex + 1 < course.courseList.length
                    ? course.courseList[targetChapterIndex + 1].lessons[0]
                    : null;

                const prevLesson =
                  targetLessonIndex - 1 >= 0
                    ? chapter.lessons[targetLessonIndex - 1]
                    : targetChapterIndex - 1 >= 0
                    ? course.courseList[targetChapterIndex - 1].lessons[
                        course.courseList[targetChapterIndex - 1].lessons.length - 1
                      ]
                    : null;

                course.videoLesson = {
                  ...targetLesson,
                  currentIndex: targetLessonIndex,
                  currentChapterIndex: targetChapterIndex,
                  nextLesson,
                  prevLesson,
                };
              }
            }

            return course || null;
          }),
          catchError((error) => {
            console.error('Error fetching course content:', error);
            return of(null);
          })
        )
      ),

      metadata: this.useTransferState(
        `course-meta-${lang}-${courseName}`,
        this.http.get<any>(metadataUrl).pipe(
          catchError((error) => {
            console.error('Error fetching metadata:', error);
            return of(null);
          })
        )
      ),
    }).pipe(
      map((result) => ({
        content: result.course,
        metadata: result.metadata,
      }))
    );
  }

  private getVideoFileName(path: string): string {
    const parts = path?.split('\\') || [];
    return parts[parts.length - 1] || '';
  }
}